import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { Tone } from './Badge';

/**
 * A block of prose the operator must read before trusting the numbers next to
 * it — a report that declares itself non-authoritative, a permission they do
 * not hold, an advisory record that does not drive the balance.
 *
 * Deliberately not a toast: a toast is an event that happened, a Callout is a
 * standing condition. It stays on screen for as long as the caveat is true.
 * Tone is semantic exactly as `Badge` — `negative` means something is wrong,
 * `warn` means proceed carefully, `info` means context.
 */
export function Callout({
  tone = 'info',
  title,
  icon,
  actions,
  children,
  className,
}: {
  tone?: Tone;
  title?: ReactNode;
  icon?: ReactNode;
  /** Right-aligned affordances on the title row. */
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const SHELL: Record<Tone, string> = {
    neutral: 'border-line bg-surface-sunken',
    signal: 'border-signal-line bg-signal-muted',
    positive: 'border-positive/30 bg-positive-muted',
    negative: 'border-negative/35 bg-negative-muted',
    warn: 'border-warn/35 bg-warn-muted',
    info: 'border-info/30 bg-info-muted',
  };
  const MARK: Record<Tone, string> = {
    neutral: 'text-ink-faint',
    signal: 'text-signal-ink',
    positive: 'text-positive',
    negative: 'text-negative',
    warn: 'text-warn',
    info: 'text-info',
  };

  return (
    <section className={cn('rounded border px-3 py-2.5', SHELL[tone], className)}>
      {title || actions ? (
        <div className="flex items-start gap-2">
          {icon ? (
            <span className={cn('mt-px shrink-0 [&_svg]:size-3.5', MARK[tone])}>
              {icon}
            </span>
          ) : null}
          <h3 className="text-ink min-w-0 flex-1 text-xs font-semibold tracking-tight">
            {title}
          </h3>
          {actions ? (
            <div className="flex shrink-0 items-center gap-1">{actions}</div>
          ) : null}
        </div>
      ) : null}
      {children ? (
        <div
          className={cn(
            'text-ink-muted text-xs leading-relaxed',
            title ? 'mt-1.5' : undefined,
            title && icon ? 'pl-5.5' : undefined
          )}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}

/**
 * The "here is what will actually happen" list. Identical vocabulary to the
 * one `ConfirmDialog` renders, extracted so an inline review step inside a
 * form dialog reads exactly like a confirmation modal.
 */
export function Consequences({
  items,
  tone = 'warn',
  className,
}: {
  items: ReactNode[];
  tone?: 'warn' | 'negative' | 'info';
  className?: string;
}) {
  if (items.length === 0) return null;
  const SHELL = {
    warn: 'border-warn/30 bg-warn-muted',
    negative: 'border-negative/30 bg-negative-muted',
    info: 'border-info/30 bg-info-muted',
  } as const;
  const MARK = {
    warn: 'text-warn',
    negative: 'text-negative',
    info: 'text-info',
  } as const;
  return (
    <ul
      className={cn(
        'text-ink space-y-1 rounded border p-2.5 text-xs',
        SHELL[tone],
        className
      )}
    >
      {/* Index keys are correct here: the list is static, ordered and never
          reordered or filtered — it is rendered once per confirmation. */}
      {items.map((item, i) => (
        <li key={i} className="flex gap-2">
          <span className={MARK[tone]}>•</span>
          <span className="min-w-0">{item}</span>
        </li>
      ))}
    </ul>
  );
}
